import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api";

const ItemInnerSubCategoryMas = ({ onSuccess, onCancel, editData, parentCategory }) => {
  const [innerSubCategoryName, setInnerSubCategoryName] = useState("");
  const [innerSubCategoryCode, setInnerSubCategoryCode] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const { skin, isDark } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const parentCategoryCode =
    parentCategory?.fld_itemCategoryCode || parentCategory?.fld_itemcategoryCode || "";

useEffect(() => {
    setInnerSubCategoryName(editData?.fld_innersubCategoryName || "");

    const rawCode = editData?.fld_innersubCategoryCode || "";
    const cleanCode = rawCode.match(/^\d+/)?.[0] || rawCode;
    setInnerSubCategoryCode(cleanCode);

    setRemarks(editData?.fld_remarks || "");

    if (!editData) {
      generateNextCode();
    }
  }, [editData]);

  const generateNextCode = async () => {
    if (!parentCategoryCode) {
      toast.info("Parent category code is missing!");
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE}/generateInnerSubCategoryCode/${parentCategoryCode}`,
        { headers: commonHeaders }
      );

      const nextCode = res.data?.nextCode;
      if (nextCode) {
        setInnerSubCategoryCode(nextCode.toString());
      } else {
        toast.error("Backend did not return next inner subcategory code.");
        setInnerSubCategoryCode("");
      }
    } catch (error) {
      console.error("Error generating code:", error);
      toast.error("Error generating inner subcategory code.");
      setInnerSubCategoryCode("");
    }
  };

  const resetForm = async () => {
    setInnerSubCategoryName("");
    setRemarks("");
    await generateNextCode();
  };

const handleSubmit = async (addAnother = false) => {
    setFormError("");

    if (!innerSubCategoryName.trim()) {
      setFormError("Inner Sub Category Name cannot be empty!");
      return;
    }
if (!innerSubCategoryCode) {
      setFormError("Inner Sub Category Code is missing!");
      return;
    }

    if (innerSubCategoryCode.length > 20) {
      setFormError("Inner Sub Category Code is too long. Please regenerate it.");
      return;
    }
    if (!parentCategory?.fld_itemsubCategoryid) {
      setFormError("Parent Sub Category ID is missing.");
      return;
    }

    setLoading(true);

    try {
      if (editData?.fld_innersubCategoryid) {
        // Update
        const payload = {
          innersubCategoryCode: innerSubCategoryCode,
          innersubCategoryName: innerSubCategoryName.trim(),
          itemsubcategoryid: Number(parentCategory.fld_itemsubCategoryid),
          modifieduserid: Number(userId) || 0,
          remarks: remarks || "",
        };

        await axios.put(
          `${API_BASE}/innerSubCategory_edit/${editData.fld_innersubCategoryid}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              ...commonHeaders,
            },
          }
        );
      } else {
        // Insert
        const payload = {
          innersubCategoryCode: innerSubCategoryCode,
          innersubCategoryName: innerSubCategoryName.trim(),
          itemsubcategoryid: parentCategory.fld_itemsubCategoryid,
          CreatedBy: Number(userId) || 0,
          remarks: remarks || "",
        };

        await axios.post(`${API_BASE}/itemInnerSubCategory_insert`, payload, {
          headers: {
            "Content-Type": "application/json",
            ...commonHeaders,
          },
        });
      }

if (addAnother && !editData) {
        setFormError("");
        toast.success("Inner subcategory saved! Add the next one.");
        await resetForm();
      } else {
        setFormError("");
        onSuccess?.();
      }
    } catch (error) {
      console.error("Error saving:", error);
      const serverMsg = error.response?.data?.message || error.response?.data?.error;

      if (
        error.response?.status === 400 &&
        serverMsg?.toLowerCase().includes("already exists")
      ) {
        setFormError("Item already exists!");
      } else {
        setFormError(serverMsg || "Error saving inner subcategory.");
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="relative font-inter w-full">
      <div
        className={`w-full relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 border ${
          isDark
            ? `${currentSkin.dark} border-slate-700/50`
            : `bg-gray-300 border-slate-200/60`
        }`}
      >
        {/* Header */}
        <div
          className={`relative px-6 py-4 border-b ${
            isDark
              ? "border-slate-700/50 bg-slate-900/60"
              : "border-slate-200/50 bg-white/60"
          } backdrop-blur-md`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md shadow-${baseColor}-500/25`}
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              </div>

              <div>
                <h3
                  className={`text-lg font-bold tracking-tight ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  {editData ? "Edit Inner Sub Category" : "Add New Inner Sub Category"}
                </h3>
                <p
                  className={`text-xs font-medium ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  {editData
                    ? "Update the inner subcategory details"
                    : "Create a new inner subcategory"}
                </p>
              </div>
            </div>

            <button
              onClick={onCancel}
              className={`group relative p-2 rounded-lg transition-all duration-300 hover:rotate-90 ${
                isDark
                  ? "text-slate-400 hover:text-white hover:bg-slate-800/80 border border-slate-700/50 hover:border-slate-600"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 border border-slate-200/50 hover:border-slate-300"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

{/* Form Body */}
        <div className="relative p-6">
          {formError && (
            <div
              className={`flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-lg border text-sm font-medium ${
                isDark
                  ? "bg-red-900/30 border-red-500/40 text-red-300"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              <span>{formError}</span>
              <button
                type="button"
                onClick={() => setFormError("")}
                className="shrink-0 opacity-70 hover:opacity-100"
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Parent Category Code */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Parent Category Code
              </label>
              <input
                type="text"
                value={parentCategoryCode}
                readOnly
                className={`w-full px-3 py-2 rounded-lg border text-sm cursor-not-allowed ${
                  isDark
                    ? "bg-slate-800/40 border-slate-600/30 text-slate-300"
                    : "bg-slate-100 border-slate-300 text-slate-600"
                }`}
              />
            </div>

            {/* Parent Sub Category Code */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Parent Sub Category Code
              </label>
              <input
                type="text"
                value={parentCategory?.fld_itemsubCategoryCode || ""}
                readOnly
                className={`w-full px-3 py-2 rounded-lg border text-sm cursor-not-allowed ${
                  isDark
                    ? "bg-slate-800/40 border-slate-600/30 text-slate-300"
                    : "bg-slate-100 border-slate-300 text-slate-600"
                }`}
              />
            </div>

            {/* Parent Sub Category Name */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Parent Sub Category Name
              </label>
              <input
                type="text"
                value={parentCategory?.fld_itemsubCategoryName || ""}
                readOnly
                className={`w-full px-3 py-2 rounded-lg border text-sm cursor-not-allowed ${
                  isDark
                    ? "bg-slate-800/40 border-slate-600/30 text-slate-300"
                    : "bg-slate-100 border-slate-300 text-slate-600"
                }`}
              />
            </div>

            {/* Inner Sub Category Code */}
            <div className="space-y-1.5">
              <label className={`flex items-center gap-1.5 text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Inner Sub Category Code
                <span className="text-red-500 font-bold">*</span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase border ${
                    isDark
                      ? "bg-emerald-900/30 text-emerald-400 border-emerald-500/30"
                      : "bg-emerald-100 text-emerald-700 border-emerald-200"
                  }`}
                >
                  Auto
                </span>
              </label>
            <input
                type="text"
                value={innerSubCategoryCode}
                disabled
                maxLength={20}
                className={`w-full px-3 py-2 rounded-lg border text-sm font-semibold tracking-wide cursor-not-allowed ${
                  isDark
                    ? "bg-slate-800/40 border-slate-600/30 text-slate-300"
                    : "bg-slate-100 border-slate-300 text-slate-600"
                }`}
                placeholder="Auto-generated"
              />
            </div>

            {/* Inner Sub Category Name */}
            <div className="xl:col-span-2 space-y-1.5">
              <label className={`flex items-center gap-1.5 text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Inner Sub Category Name
                <span className="text-red-500 font-bold">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={innerSubCategoryName}
                  onChange={(e) => setInnerSubCategoryName(e.target.value)}
                  maxLength={50}
                  className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
                      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
                  }`}
                  placeholder="Enter inner subcategory name"
                />
                <div className={`absolute -bottom-4 right-0 text-[10px] font-medium ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                  {innerSubCategoryName.length}/50
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div className="xl:col-span-2 space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Remarks
              </label>
              <div className="relative">
                <textarea
  value={remarks}
  onChange={(e) => setRemarks(e.target.value)}
  rows={2}
  maxLength={250}
  className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 resize-none ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`}
  placeholder="Optional notes..."
/>
<div className={`absolute -bottom-4 right-0 text-[10px] font-medium ${isDark ? "text-slate-500" : "text-slate-400"}`}>
  {remarks.length}/250
</div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-700/60">
            <button
              onClick={onCancel}
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              Cancel
            </button>

            {/* {!editData && (
              <button
                onClick={() => handleSubmit(true)}
                disabled={loading}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg shadow transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed bg-slate-400" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Save & Add New
              </button>
            )} */}

            <button
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className={`px-5 py-2 text-sm font-semibold text-white rounded-lg shadow transition-colors ${
                loading
                  ? "opacity-50 cursor-not-allowed bg-slate-400"
                  : `bg-${baseColor}-600 hover:bg-${baseColor}-700`
              }`}
            >
              {loading ? "Saving..." : editData ? "Update Inner Sub Category" : "Save Inner Sub Category"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemInnerSubCategoryMas;